import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class TtsStreamingService {
  /**
   * Streams TTS audio from the backend response using the provided audio element.
   *
   * @param response The Response object from the backend TTS endpoint.
   * @param audioElement The HTMLAudioElement used for playback.
   * @returns A Promise that resolves when playback ends.
   */
  public streamTTS(response: Response, audioElement: HTMLAudioElement): Promise<void> {
    return new Promise(async (resolve, reject) => {
      if (!response.body) {
        console.error("No response body for TTS streaming");
        return resolve();
      }

      const onPlaybackEnded = () => {
        audioElement.removeEventListener("ended", onPlaybackEnded);
        resolve();
      };
      audioElement.addEventListener("ended", onPlaybackEnded);

      if (typeof MediaSource !== "undefined") {
        const mediaSource = new MediaSource();
        audioElement.src = URL.createObjectURL(mediaSource);

        mediaSource.addEventListener(
          "sourceopen",
          async () => {
            let sourceBuffer: SourceBuffer;
            try {
              sourceBuffer = mediaSource.addSourceBuffer("audio/aac");
            } catch (e) {
              console.error("Error adding sourceBuffer:", e);
              reject(e);
              return;
            }
            const reader = response.body!.getReader();

            const processChunk = async () => {
              if (audioElement.error || mediaSource.readyState !== "open") {
                console.error("Audio element or MediaSource in error state; stopping TTS streaming.");
                this.tryEndOfStream(mediaSource, sourceBuffer);
                return;
              }
              try {
                const { done, value } = await reader.read();
                if (done) {
                  this.tryEndOfStream(mediaSource, sourceBuffer);
                  return;
                }
                try {
                  if (!sourceBuffer.updating) {
                    sourceBuffer.appendBuffer(value);
                  } else {
                    // Wait a bit if the sourceBuffer is busy.
                    setTimeout(processChunk, 250);
                    return;
                  }
                } catch (error) {
                  console.error("Error appending TTS chunk:", error);
                  this.tryEndOfStream(mediaSource, sourceBuffer);
                  return;
                }
                setTimeout(processChunk, 10);
              } catch (error) {
                console.error("Error reading TTS stream:", error);
                this.tryEndOfStream(mediaSource, sourceBuffer);
              }
            };

            audioElement.play().catch((err) => console.error("TTS playback error:", err));
            await processChunk();
          },
          { once: true }
        );
      } else {
        try {
          const responseBlob = await response.blob();
          const audioUrl = URL.createObjectURL(responseBlob);
          audioElement.src = audioUrl;
          audioElement.play().catch((err) => {
            console.error("Fallback playback error:", err);
            resolve();
          });
        } catch (error) {
          console.error("Error during fallback TTS streaming:", error);
          reject(error);
        }
      }
    });
  }

  /**
   * Helper to end the MediaSource stream gracefully.
   *
   * @param mediaSource The MediaSource instance.
   * @param sourceBuffer The SourceBuffer used for streaming.
   */
  private tryEndOfStream(mediaSource: MediaSource, sourceBuffer: SourceBuffer) {
    if (!sourceBuffer.updating) {
      if (mediaSource.readyState === "open") {
        try {
          mediaSource.endOfStream();
        } catch (e) {
          console.error("Error ending MediaSource stream:", e);
        }
      }
    } else {
      setTimeout(() => this.tryEndOfStream(mediaSource, sourceBuffer), 100);
    }
  }
}
